import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { yupResolver } from '@hookform/resolvers/yup';
import { AddCircle, Edit } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { getCreateVacationMutationOptions, getUpdateVacationMutationOptions, isLoading, onValidationError, useCreateVacationMutation, useSchoolYearQueryApi, useUpdateVacationMutation, } from 'api';
import { ConfirmationDialog, DatePicker, LoadingWrapper, TextFormItem, } from 'components/custom';
import { setHours, setMinutes } from 'date-fns';
import { isEmpty } from 'lodash-es';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useDialogMode } from 'utils/dialog-form';
import { onSubmitError, yup } from 'utils/forms';
const SaveVacationDialog = (props) => {
    const { activeVacation, close } = props;
    const { t } = useTranslation('timetable', {
        keyPrefix: 'steps.vacation.cmp.SaveVacationDialog',
    });
    const id = React.useId();
    const { modeCreate, open } = useDialogMode(activeVacation);
    const { schoolYearId = '' } = useParams();
    const schoolYearStatus = useSchoolYearQueryApi({ id: schoolYearId });
    const [createVacation, createVacationStatus] = useCreateVacationMutation();
    const [updateVacation, updateVacationStatus] = useUpdateVacationMutation();
    const schema = React.useMemo(() => yup.object({
        name: yup.string().label(t('nameField.label')).required(),
        startDate: yup
            .date()
            .label(t('startField.label'))
            .required()
            .when([], {
            is: () => !isEmpty(schoolYearStatus.data),
            then: (dateSchema) => {
                if (!schoolYearStatus.data?.startDate) {
                    return dateSchema;
                }
                return dateSchema.min(setMinutes(setHours(schoolYearStatus.data.startDate, 0), 0).toISOString());
            },
        }),
        // eslint-disable-next-line sort-keys -- sorted by field occurrence
        endDate: yup
            .date()
            .label(t('endField.label'))
            .required()
            .when([], {
            is: () => !isEmpty(schoolYearStatus.data),
            then: (dateSchema) => {
                if (!schoolYearStatus.data?.endDate) {
                    return dateSchema;
                }
                return dateSchema.max(schoolYearStatus.data.endDate.toISOString());
            },
        }),
    }), [schoolYearStatus, t]);
    const { handleSubmit, control, formState: { errors, isSubmitting }, reset, } = useForm({
        resolver: yupResolver(schema),
    });
    React.useEffect(() => {
        reset(activeVacation ?? {});
    }, [activeVacation, reset]);
    const mutationStatus = modeCreate
        ? createVacationStatus
        : updateVacationStatus;
    const onSuccess = () => {
        close();
        enqueueSnackbar(t('successMessage'));
    };
    const onSubmit = async (values) => {
        if (activeVacation?.id) {
            return updateVacation(getUpdateVacationMutationOptions({
                ...values,
                id: activeVacation.id,
            }))
                .then(onSuccess)
                .catch(onValidationError);
        }
        return createVacation(getCreateVacationMutationOptions({ ...values, schoolYearId }))
            .then(onSuccess)
            .catch(onValidationError);
    };
    const loading = isLoading([
        schoolYearStatus,
        updateVacationStatus,
        createVacationStatus,
    ]);
    return (_jsx(ConfirmationDialog, { open: open, onClose: close, onTransitionExited: () => {
            mutationStatus.reset();
        }, titleProps: {
            Icon: modeCreate ? AddCircle : Edit,
            text: modeCreate ? t('addVacationTitle') : t('editVacationTitle'),
        }, cancelButtonProps: {
            onClick: close,
        }, confirmButtonProps: {
            disabled: isSubmitting || loading,
            form: id,
            type: 'submit',
        }, fullWidth: true, children: _jsx(LoadingWrapper, { status: schoolYearStatus, mutation: mutationStatus, children: _jsx("form", { id: id, onSubmit: (e) => {
                    handleSubmit(onSubmit)(e).catch(onSubmitError);
                }, children: _jsxs(Stack, { spacing: 2, children: [_jsx(Controller, { control: control, name: "name", render: ({ field }) => (_jsx(TextFormItem, { ...field, label: t('nameField.label'), placeholder: t('nameField.placeholder'), errorMessage: errors.name?.message })) }), _jsx(Controller, { control: control, name: "startDate", render: ({ field }) => (_jsx(DatePicker, { ...field, label: t('startField.label'), errorMessage: errors.startDate?.message, minDate: schoolYearStatus.data?.startDate, maxDate: schoolYearStatus.data?.endDate })) }), _jsx(Controller, { control: control, name: "endDate", render: ({ field }) => (_jsx(DatePicker, { ...field, label: t('endField.label'), errorMessage: errors.endDate?.message, minDate: schoolYearStatus.data?.startDate, maxDate: schoolYearStatus.data?.endDate })) })] }) }) }) }));
};
export default SaveVacationDialog;
